import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Dict, Docmnt } from '/imports/types';
import { strings } from '/imports/strings';
import { MethodDeclaration } from '/imports/methods';
import { arrayToCsv, capitalize, clientDownload, defer, filterBoolean, formatDateTime, randomString, roundNumber } from '/imports/utils';
import { Table } from '/imports/ui/Table';
import { Checkbox } from '/imports/ui/Checkbox';
import { FormInput } from '/imports/ui/FormBuilder';
import { Button } from '/imports/ui/Button';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { Icon } from '/imports/ui/Icon';

export type Column<T extends Docmnt = any> = {
  field: keyof T;
  label?: string | (() => string | React.ReactNode);
  input?: FormInput;
  render?(d: T, r?: Dict<Dict<Docmnt>>): string | React.ReactNode;
  renderEditable?(d: T): React.ReactNode;
  width?: string;
} | null

export interface DataTableProps<T extends Docmnt = any> {
  data: T[];
  references?: Dict<Dict<Docmnt>>;
  allIds: string[];
  columns: Column<T>[];
  pageSize?: number;
  filters?: ((d: T) => boolean | null | undefined)[];
  updateMethod?: MethodDeclaration<Partial<T>, string>;
  deleteMethod?: MethodDeclaration<string, void>;
  sort?: { [k in keyof T]?: 1 | -1 };
  loading?: boolean;
  softLoading?: boolean;
  element?: (d: T) => React.ReactNode;
  row?: (d: T) => React.ReactNode;
  rowClass?: (d: T) => string;
  onSelectedIds?(selectedIds: string[]): void;
  defaultSelectedIds?: string[];
  onScrollBottom?(): void;
  onClickColumn?(column: Column): void;
  exportable?: boolean;
}

/**
 * Generic, virtualized, sortable/filterable table with inline editing and CSV export.
 */
export const DataTable = <T extends Docmnt,>(props: DataTableProps<T>) => {
  const { data, references, columns, filters, pageSize, onScrollBottom, onSelectedIds, updateMethod,
    onClickColumn, loading, softLoading, element, row, rowClass, allIds, exportable } = props;
  const _pageSize = pageSize || 100;
  const _filters = filters || [];
  const [tableId,] = useState(`table-${randomString()}`);
  const [internalSort, setInternalSort] = useState<{ field: string; dir: 1 | -1 } | null>(null);
  const [limit, setLimit] = useState(_pageSize);
  const [selectedIds, setSelectedIds] = useState<string[]>(props.defaultSelectedIds || []);
  const [enableEdit, setEnableEdit] = useState<Dict<{ [K in keyof T]: boolean }>>({});
  const [editing, setEditing] = useState<Dict<{ [K in keyof T]: any }>>({});
  const [edited, setEdited] = useState<Dict<{ [K in keyof T]: any }>>({});
  const tableRef = useRef<HTMLTableElement>(null);
  const rowObserver = useRowObserver(tableId);
  let filteredData = data;
  for (const filter of _filters) filteredData = filteredData.filter(filter);
  if (!onClickColumn && internalSort) {
    const { field, dir } = internalSort;
    filteredData = [...filteredData].sort((a, b) => {
      const av = (a as any)[field];
      const bv = (b as any)[field];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return -dir;
      if (av > bv) return dir;
      return 0;
    });
  }
  /**
   * Cycles a column's client-side sort: none -> asc -> desc -> none.
   * @param field - column field to sort by
   */
  const toggleSort = (field: string) => {
    if (!internalSort || internalSort.field !== field) setInternalSort({ field, dir: 1 });
    else if (internalSort.dir === 1) setInternalSort({ field, dir: -1 });
    else setInternalSort(null);
  };
  useEffect(() => { onSelectedIds?.(selectedIds); }, [selectedIds]);
  const [fixedWidths, setFixedWidths] = useState(false);
  useEffect(() => {
    if (fixedWidths) return;
    if (loading) return;
    if (!tableRef.current) return;
    const ths = Array.from(tableRef.current.querySelectorAll('th')).filter((th) => !th.style.width);
    for (const th of ths) th.style.width = `${roundNumber(th.getBoundingClientRect().width)}px`;
    setFixedWidths(true);
  }, [loading]);
  /**
   * Focuses the inline-edit input for a given row/field.
   * @param _id - row document id
   * @param field - column field being edited
   */
  const focusInline = useCallback((_id: string, field: keyof T) => {
    defer(() => document.getElementById(`inline_${_id}_${field as string}`)?.querySelector('input')?.focus());
  }, []);
  /**
   * Persists a row's inline edits via updateMethod and clears its edit state.
   * @param _id - row document id
   */
  const saveDocument = useCallback(async (_id: string) => {
    try {
      await updateMethod?.call({ ...editing[_id], _id });
      setEdited({ ...edited, [_id]: editing[_id] });
      delete editing[_id];
      delete enableEdit[_id];
      setEditing({ ...editing });
      setEnableEdit({ ...enableEdit });
      toast.success(strings.Saved());
    }
    catch (error: any) {
      console.log(error);
      toast.error(`${error}`);
    }
  }, []);
  /**
   * Returns the sort-indicator CSS class for a column header.
   * @param field - column field
   * @returns 'sort-asc', 'sort-desc', or ''
   */
  const columnSortClass = (field: string) => {
    if (!onClickColumn && internalSort?.field === field) return internalSort.dir === 1 ? 'sort-asc' : 'sort-desc';
    const sort = props.sort || {} as any;
    if (sort[field] === 1) return 'sort-asc';
    if (sort[field] === -1) return 'sort-desc';
    return '';
  };
  /** Downloads the currently filtered rows as a CSV file. */
  const exportToCsv = () => {
    const csv = arrayToCsv(filteredData);
    clientDownload(csv, `Export-${Date.now()}`, 'text/csv');
  };
  return (
    <Table id={tableId} onScrollBottom={onScrollBottom || (() => setLimit(limit + _pageSize))}>
      {
        element ?
          <div className="custom-table-elements">
            {filteredData.map((d) => element(d))}
          </div>
          :
          <table ref={tableRef}>
            <thead>
              <tr>
                <th className="row-select">
                  <Checkbox onChange={(checked) => {
                    setSelectedIds(checked ? [...allIds] : []);
                  }} />
                </th>
                {
                  filterBoolean(columns).map((c) => {
                    const _label = typeof c.label === 'function' ? c.label() : c.label || (strings as any)[capitalize(c.field as string)]?.() || `_${String(c.field)}_`;
                    return (
                      <th key={c.field as string} className={columnSortClass(c.field as string)} style={{ width: c.width }} onClick={() => onClickColumn ? onClickColumn(c) : toggleSort(c.field as string)}>
                        {_label}
                      </th>
                    );
                  })
                }
                <th className="row-actions">
                  {
                    exportable !== false ? <span className="export-icon" onClick={() => exportToCsv()}><Icon icon="download" tip={strings.ExportToCsv()} /></span> : null
                  }
                </th>
              </tr>

            </thead>
            <tbody className={`${loading ? 'loading' : ''}${softLoading ? ' soft-loading' : ''}`}>
              {
                loading ?
                  <tr><td colSpan={columns.length + 2}><LoadingFlex /></td></tr>
                  :
                  filteredData.map((d) => {
                    return row?.(d) || (
                      <TableRow
                        key={d._id}
                        d={d}
                        selectedIds={selectedIds}
                        setSelectedIds={setSelectedIds}
                        enableEdit={enableEdit}
                        setEnableEdit={setEnableEdit as any}
                        editing={editing}
                        setEditing={setEditing as any}
                        edited={edited}
                        focusInline={focusInline as any}
                        saveDocument={saveDocument}
                        columns={columns}
                        rowClass={rowClass}
                        references={references}
                        rowObserver={rowObserver}
                      />
                    );
                  })
              }
            </tbody>
          </table>
      }
    </Table>
  );
};

type TableRowProps<T extends Docmnt = any> = {
  d: T,
  selectedIds: string[],
  setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>,
  enableEdit: Dict<{ [K in keyof T]: boolean; }>,
  setEnableEdit: React.Dispatch<React.SetStateAction<Dict<{ [K in keyof T]: boolean; }>>>,
  editing: Dict<{ [K in keyof T]: any }>,
  setEditing: React.Dispatch<React.SetStateAction<Dict<{ [K in keyof T]: boolean; }>>>,
  edited: Dict<{ [K in keyof T]: any }>,
  focusInline: (_id: string, field: keyof T) => void,
  saveDocument: (_id: string) => Promise<void>,
  rowObserver: RowObserver,
} & Pick<DataTableProps<T>, 'rowClass' | 'columns' | 'updateMethod' | 'references'>;

/** Distance outside the scrolling viewport at which a row starts drawing its cells. */
const SHOW_MARGIN = 400;
/**
 * Distance at which a drawn row is allowed to blank again. Deliberately far outside SHOW_MARGIN: the gap
 * between the two is the hysteresis that keeps the decisions from chasing each other, see useRowObserver.
 */
const HIDE_MARGIN = 2400;

/**
 * Finds the element that actually scrolls `el`, or null when the page itself does.
 *
 * Which of the two it is matters more than it looks. An IntersectionObserver honours the clipping of
 * every ancestor between the target and its root, but rootMargin only ever inflates the ROOT — so an
 * observer rooted at the viewport while the rows are clipped by a scrolling div gets nothing at all out
 * of its margin, however large, and rows draw only once they are already on screen.
 * @param el - element to walk up from
 * @returns the scrolling ancestor, or null meaning the viewport
 */
const scrollParent = (el: Element | null): Element | null => {
  for (let node = el; node && node !== document.body && node !== document.documentElement; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) return node;
  }
  return null;
};

type RowObserver = { observe(el: Element, notify: (visible: boolean) => void): () => void };

/**
 * Builds the pair of IntersectionObservers that every row of one table shares.
 *
 * One pair per TABLE rather than one per row: an observer instance per row was a good part of the cost
 * the virtualization set out to save in the first place.
 *
 * The pair is also what stops the flickering. A blank row is far shorter than a drawn one — 25px against
 * 70px on a typical table here — so a single show/hide boundary lets the geometry feed back on itself:
 * rows draw, the ones above them grow, everything below slides down past the boundary and blanks, the
 * column shortens, and they slide back up and draw again, several times a frame. It only bites at the
 * window heights that leave a row sitting exactly on the boundary, which is what makes it look so
 * arbitrary. Drawing at SHOW_MARGIN but blanking only past HIDE_MARGIN puts 2000px between the two
 * decisions, which no run of row-height changes can cover, so the loop has nowhere to close.
 * @param tableId - dom id of the table's scroll container
 * @returns the facade rows register themselves with
 */
const useRowObserver = (tableId: string): RowObserver => {
  const state = useRef({
    rows: new Map<Element, { notify: (visible: boolean) => void, visible: boolean }>(),
    observers: [] as IntersectionObserver[],
  }).current;
  useEffect(() => () => state.observers.forEach((o) => o.disconnect()), []);
  return useMemo(() => ({
    observe(el: Element, notify: (visible: boolean) => void) {
      if (!state.observers.length) {
        // Resolved on first registration rather than at mount: the table is in the dom at its real height
        // by then, so the walk up can tell a table that scrolls itself from one the page scrolls for it.
        const root = scrollParent(document.getElementById(tableId));
        const build = (margin: number, reports: boolean) => new IntersectionObserver((entries) => {
          for (const entry of entries) {
            const row = state.rows.get(entry.target);
            // Each observer only ever speaks for its own half of the hysteresis, and only on a change:
            // on mount every off-screen row reports out of view, which is already what it thinks it is.
            if (entry.isIntersecting !== reports || !row || row.visible === reports) continue;
            row.visible = reports;
            row.notify(reports);
          }
        }, { root, threshold: 0, rootMargin: `${margin}px 0px` });
        state.observers = [build(SHOW_MARGIN, true), build(HIDE_MARGIN, false)];
      }
      state.rows.set(el, { notify, visible: false });
      state.observers.forEach((o) => o.observe(el));
      return () => {
        state.rows.delete(el);
        state.observers.forEach((o) => o.unobserve(el));
      };
    },
  }), []);
};

/**
 * A single table row that only draws its cells once scrolled into view.
 */
const _TableRow = (props: TableRowProps) => {
  const {
    d, rowClass, references, columns, updateMethod, selectedIds, setSelectedIds, enableEdit, setEnableEdit, editing, setEditing,
    edited, focusInline, saveDocument, rowObserver
  } = props;
  const tr = useRef<HTMLTableRowElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  // The height the row had while it was drawn, pinned back onto the blank placeholder. Without it every
  // row that scrolls out shortens and drags the rest up under the reader, which is both a visible jump
  // and the one remaining way the geometry could still feed the loop the two margins above rule out.
  const drawnHeight = useRef<number | undefined>(undefined);
  useEffect(() => (tr.current ? rowObserver.observe(tr.current, setIsVisible) : undefined), []);
  useEffect(() => {
    if (isVisible && tr.current) drawnHeight.current = tr.current.getBoundingClientRect().height;
  }, [isVisible]);
  return (
    <tr ref={tr} key={d._id} className={rowClass?.(d) || ''} style={isVisible ? undefined : { height: drawnHeight.current }}>
      {
        isVisible ?
          <td className="row-select">
            <Checkbox
              checked={selectedIds.includes(d._id)}
              onChange={(checked) => {
                // Todo: Handle shift key to bulk-select
                if (checked) setSelectedIds([...selectedIds, d._id]);
                else setSelectedIds(selectedIds.filter((_id) => _id !== d._id));
              }}
            />
          </td>
          :
          <td className="row-select" />
      }
      {
        filterBoolean(columns).map((c) => {
          if ((c.renderEditable || c.input) && updateMethod && enableEdit[d._id]?.[c.field as string]) {
            const _input = c.input as any;
            return isVisible ?
              <td key={c.field as string}>
                {
                  c.renderEditable ?
                    c.renderEditable(d)
                    :
                    _input?.type === Checkbox ?
                      React.createElement(Checkbox as any, {
                        id: `inline_${d._id}_${c.field as string}`,
                        checked: editing[d._id]?.[c.field as string],
                        onChange: (value: any) => setEditing({ ...editing, [d._id]: { ...editing[d._id], [c.field]: value } }),
                        ..._input?.props,
                      })
                      :
                      React.createElement(_input?.type, {
                        id: `inline_${d._id}_${c.field as string}`,
                        value: editing[d._id]?.[c.field as string],
                        onChange: (value: any) => setEditing({ ...editing, [d._id]: { ...editing[d._id], [c.field]: value } }),
                        ..._input?.props,
                      })
                }
              </td>
              :
              <td key={c.field as string} />;
          }
          else {
            const doc = { ...d, ...edited[d._id] };
            const value = doc[c.field];
            return isVisible ?
              <td key={c.field as string} style={{ width: c.width }} onDoubleClick={() => {
                if (!c.renderEditable && !c.input) return;
                setEnableEdit({ ...editing, [d._id]: { ...editing[d._id], [c.field]: true } });
                setEditing({ ...editing, [d._id]: { ...editing[d._id], [c.field]: value } });
                focusInline(d._id, c.field);
              }}>
                {c.render ? c.render(doc, references) : (value as any instanceof Date ? formatDateTime(value) : value)}
              </td>
              :
              <td key={c.field as string} style={{ width: c.width }} />;
          }
        })
      }
      <td className="row-actions">
        {updateMethod && editing[d._id] && <Button onClick={() => saveDocument(d._id)}>{strings.Save()}</Button>}
      </td>
    </tr>
  );
};

const TableRow = React.memo(_TableRow);